/**
 * Branding Configuration
 *
 * Central configuration for multi-brand deployment.
 * Switch the active brand by changing `ACTIVE_BRAND` below.
 *
 * Supported brands: 'q2bi' | 'jdix' | 'xiaoyuan'
 */

import i18n from '@/i18n/config';

export type BrandKey = 'q2bi' | 'jdix' | 'xiaoyuan';

export interface BrandLogoStyle {
  /** CSS width (px) */
  width: number;
  /** CSS height (px) */
  height: number;
  /** Optional extra CSS classes */
  className?: string;
}

export interface BrandI18n {
  /** common.appName */
  appName: string;
  /** chat.disclaimer */
  disclaimer: string;
  /** chat.replyPlaceholder */
  replyPlaceholder: string;
  /** feedback.submitInfo */
  submitInfo: string;
  /** login.privacyNotice */
  privacyNotice: string;
  /** toast.copyright */
  toastCopyright: string;
  /** version.copyright */
  versionCopyright: string;
}

export interface BrandingConfig {
  /** Brand identifier */
  key: BrandKey;
  /** Display name shown in sidebar header, page title, etc. */
  name: string;
  /** Short name used in i18n placeholders (e.g. "Message {shortName}") */
  shortName: string;
  /** Path to logo image (relative to public/) */
  logo: string;
  /** Path to favicon */
  favicon: string;
  /** HTML document title */
  documentTitle: string;
  /** Logo display style for sidebar collapsed state */
  sidebarCollapsedLogoStyle: BrandLogoStyle;
  /** Logo display style for sidebar expanded state */
  sidebarExpandedLogoStyle: BrandLogoStyle;
  /** Logo display style for welcome screen / login */
  welcomeLogoStyle: BrandLogoStyle;
  /** Whether to show brand name text next to logo in sidebar */
  showNameInSidebar: boolean;
  /** Optional: override text shown next to logo in sidebar (defaults to `name` if not set) */
  sidebarText?: string;
  /** Optional: extra inline style for the sidebar text (e.g. vertical alignment tweaks) */
  sidebarTextStyle?: React.CSSProperties;
  /** Optional: override text shown next to logo on welcome/login/privacy pages (defaults to `name` if not set) */
  logoText?: string;
  /** Optional: extra inline style for the logo-accompanying text on welcome/login pages */
  logoTextStyle?: React.CSSProperties;
  /** Brand accent color (hex) - used for New Chat button, icons, etc. */
  accentColor: string;
  /** Brand accent color with low opacity (hex with alpha) - used for hover backgrounds */
  accentColorHover: string;
  /** i18n overrides per locale */
  i18n: {
    en: BrandI18n;
    zh: BrandI18n;
  };
}

// ---------------------------------------------------------------------------
// Brand Presets
// ---------------------------------------------------------------------------

const q2biBranding: BrandingConfig = {
  key: 'q2bi',
  name: 'Q2 Agents',
  shortName: 'Q2BI',
  logo: '/static/q2bi-logo.png',
  favicon: '/static/q2bi-logo.png',
  documentTitle: 'Q2 Agents',
  sidebarCollapsedLogoStyle: {
    width: 24,
    height: 24,
    className: 'rounded object-contain',
  },
  sidebarExpandedLogoStyle: {
    width: 24,
    height: 24,
    className: 'rounded object-contain',
  },
  welcomeLogoStyle: {
    width: 56,
    height: 56,
    className: 'rounded-xl object-contain',
  },
  showNameInSidebar: true,
  accentColor: '#c96442',
  accentColorHover: '#c9644214',
  i18n: {
    en: {
      appName: 'Q2 Agents',
      disclaimer:
        'Q2 Agents can make mistakes. Please verify and validate the generated results.',
      replyPlaceholder: 'Message Q2BI',
      submitInfo:
        'Submitting this report will send the entire current conversation to Q2BI for future improvements to our models.',
      privacyNotice:
        "By continuing, you acknowledge Q2BI's <privacyLink>Privacy Policy</privacyLink> and agree to get occasional product update and promotional emails.",
      toastCopyright: '\u00a9 2026 Q2 Agents. All rights reserved.',
      versionCopyright: '\u00a9 2026 Q2 Agents. All rights reserved.',
    },
    zh: {
      appName: 'Q2 \u667a\u80fd\u52a9\u624b',
      disclaimer:
        'Q2Agent \u53ef\u80fd\u4f1a\u51fa\u9519\u3002\u8bf7\u9a8c\u8bc1\u548c\u6838\u5b9e\u751f\u6210\u7ed3\u679c\u3002',
      replyPlaceholder: '\u7ed9 Q2BI \u53d1\u9001\u6d88\u606f',
      submitInfo:
        '\u63d0\u4ea4\u6b64\u62a5\u544a\u5c06\u4f1a\u628a\u5f53\u524d\u5bf9\u8bdd\u53d1\u9001\u7ed9 Q2BI\uff0c\u7528\u4e8e\u6539\u8fdb\u6211\u4eec\u7684\u6a21\u578b\u3002',
      privacyNotice:
        '\u7ee7\u7eed\u5373\u8868\u793a\u60a8\u786e\u8ba4 Q2BI \u7684<privacyLink>\u9690\u79c1\u653f\u7b56</privacyLink>\u5e76\u540c\u610f\u63a5\u6536\u5076\u5c14\u7684\u4ea7\u54c1\u66f4\u65b0\u548c\u4fc3\u9500\u90ae\u4ef6\u3002',
      toastCopyright:
        '\u00a9 2026 Q2 Agents. \u4fdd\u7559\u6240\u6709\u6743\u5229\u3002',
      versionCopyright:
        '\u00a9 2026 Q2 Agents. \u4fdd\u7559\u6240\u6709\u6743\u5229\u3002',
    },
  },
};

const jdixBranding: BrandingConfig = {
  key: 'jdix',
  name: 'JDIX Agents',
  shortName: 'JDIX',
  logo: '/static/jdix-logo.png',
  favicon: '/static/jdix-logo.png',
  documentTitle: 'JDIX Agents',
  sidebarCollapsedLogoStyle: {
    width: 32,
    height: 12,
    className: 'object-contain',
  },
  sidebarExpandedLogoStyle: {
    width: 48,
    height: 18,
    className: 'object-contain',
  },
  welcomeLogoStyle: {
    width: 120,
    height: 44,
    className: 'object-contain',
  },
  showNameInSidebar: true,
  sidebarText: 'Agents',
  sidebarTextStyle: { marginTop: 2 },
  logoText: 'Agents',
  logoTextStyle: { marginTop: 4 },
  accentColor: '#1e40af',
  accentColorHover: '#1e40af18',
  i18n: {
    en: {
      appName: 'JDIX Agents',
      disclaimer:
        'JDIX Agents can make mistakes. Please verify and validate the generated results.',
      replyPlaceholder: 'Message JDIX',
      submitInfo:
        'Submitting this report will send the entire current conversation to JDIX for future improvements to our models.',
      privacyNotice:
        "By continuing, you acknowledge JDIX's <privacyLink>Privacy Policy</privacyLink> and agree to get occasional product update and promotional emails.",
      toastCopyright: '\u00a9 2026 JDIX Agents. All rights reserved.',
      versionCopyright: '\u00a9 2026 JDIX Agents. All rights reserved.',
    },
    zh: {
      appName: 'JDIX \u667a\u80fd\u52a9\u624b',
      disclaimer:
        'JDIX Agent \u53ef\u80fd\u4f1a\u51fa\u9519\u3002\u8bf7\u9a8c\u8bc1\u548c\u6838\u5b9e\u751f\u6210\u7ed3\u679c\u3002',
      replyPlaceholder: '\u7ed9 JDIX \u53d1\u9001\u6d88\u606f',
      submitInfo:
        '\u63d0\u4ea4\u6b64\u62a5\u544a\u5c06\u4f1a\u628a\u5f53\u524d\u5bf9\u8bdd\u53d1\u9001\u7ed9 JDIX\uff0c\u7528\u4e8e\u6539\u8fdb\u6211\u4eec\u7684\u6a21\u578b\u3002',
      privacyNotice:
        '\u7ee7\u7eed\u5373\u8868\u793a\u60a8\u786e\u8ba4 JDIX \u7684<privacyLink>\u9690\u79c1\u653f\u7b56</privacyLink>\u5e76\u540c\u610f\u63a5\u6536\u5076\u5c14\u7684\u4ea7\u54c1\u66f4\u65b0\u548c\u4fc3\u9500\u90ae\u4ef6\u3002',
      toastCopyright:
        '\u00a9 2026 JDIX Agents. \u4fdd\u7559\u6240\u6709\u6743\u5229\u3002',
      versionCopyright:
        '\u00a9 2026 JDIX Agents. \u4fdd\u7559\u6240\u6709\u6743\u5229\u3002',
    },
  },
};

const xiaoyuanBranding: BrandingConfig = {
  key: 'xiaoyuan',
  name: 'Xiaoyuan Agents',
  shortName: 'Xiaoyuan',
  logo: '/static/xiaoyuan.png',
  favicon: '/static/xiaoyuan.png',
  documentTitle: 'Xiaoyuan Agents',
  sidebarCollapsedLogoStyle: {
    width: 56,
    height: 24,
    className: 'object-contain',
  },
  sidebarExpandedLogoStyle: {
    width: 90,
    height: 38,
    className: 'object-contain',
  },
  welcomeLogoStyle: {
    width: 180,
    height: 77,
    className: 'object-contain',
  },
  showNameInSidebar: true,
  sidebarText: 'Agents',
  accentColor: '#6d28d9',
  accentColorHover: '#6d28d918',
  i18n: {
    en: {
      appName: 'Xiaoyuan Agents',
      disclaimer:
        'Xiaoyuan Agents can make mistakes. Please verify and validate the generated results.',
      replyPlaceholder: 'Message Xiaoyuan',
      submitInfo:
        'Submitting this report will send the entire current conversation to Xiaoyuan for future improvements to our models.',
      privacyNotice:
        "By continuing, you acknowledge Xiaoyuan's <privacyLink>Privacy Policy</privacyLink> and agree to get occasional product update and promotional emails.",
      toastCopyright: '\u00a9 2026 Xiaoyuan Agents. All rights reserved.',
      versionCopyright: '\u00a9 2026 Xiaoyuan Agents. All rights reserved.',
    },
    zh: {
      appName: 'Xiaoyuan \u667a\u80fd\u52a9\u624b',
      disclaimer:
        'Xiaoyuan Agent \u53ef\u80fd\u4f1a\u51fa\u9519\u3002\u8bf7\u9a8c\u8bc1\u548c\u6838\u5b9e\u751f\u6210\u7ed3\u679c\u3002',
      replyPlaceholder: '\u7ed9 Xiaoyuan \u53d1\u9001\u6d88\u606f',
      submitInfo:
        '\u63d0\u4ea4\u6b64\u62a5\u544a\u5c06\u4f1a\u628a\u5f53\u524d\u5bf9\u8bdd\u53d1\u9001\u7ed9 Xiaoyuan\uff0c\u7528\u4e8e\u6539\u8fdb\u6211\u4eec\u7684\u6a21\u578b\u3002',
      privacyNotice:
        '\u7ee7\u7eed\u5373\u8868\u793a\u60a8\u786e\u8ba4 Xiaoyuan \u7684<privacyLink>\u9690\u79c1\u653f\u7b56</privacyLink>\u5e76\u540c\u610f\u63a5\u6536\u5076\u5c14\u7684\u4ea7\u54c1\u66f4\u65b0\u548c\u4fc3\u9500\u90ae\u4ef6\u3002',
      toastCopyright:
        '\u00a9 2026 Xiaoyuan Agents. \u4fdd\u7559\u6240\u6709\u6743\u5229\u3002',
      versionCopyright:
        '\u00a9 2026 Xiaoyuan Agents. \u4fdd\u7559\u6240\u6709\u6743\u5229\u3002',
    },
  },
};

// ---------------------------------------------------------------------------
// Brand Registry
// ---------------------------------------------------------------------------

export const BRAND_CONFIGS: Record<BrandKey, BrandingConfig> = {
  q2bi: q2biBranding,
  jdix: jdixBranding,
  xiaoyuan: xiaoyuanBranding,
};

// ---------------------------------------------------------------------------
// Active Brand - Change this value to switch deployment branding
// ---------------------------------------------------------------------------

export const ACTIVE_BRAND: BrandKey = 'xiaoyuan';

// ---------------------------------------------------------------------------
// Convenience Exports
// ---------------------------------------------------------------------------

/** Current active branding configuration */
export const branding: BrandingConfig = BRAND_CONFIGS[ACTIVE_BRAND];

/**
 * Apply branding-specific runtime settings.
 * Called once on app initialization (in main.tsx).
 *
 * - Sets document title & favicon
 * - Dynamically overrides i18n keys with brand-specific values
 */
export function applyBrandingTheme(): void {
  // Set document title
  document.title = branding.documentTitle;

  // Save full brand config to localStorage so static HTML pages
  // (privacy-policy.html) can read the same brand without needing
  // a separate config file.
  try {
    localStorage.setItem(
      '__BRAND_ACTIVE__',
      JSON.stringify({
        active: branding.key,
        name: branding.name,
        logo: branding.logo,
        logoWidth: branding.welcomeLogoStyle.width,
        logoHeight: branding.welcomeLogoStyle.height,
        accentColor: branding.accentColor,
      })
    );
  } catch (_e) {
    // localStorage may be unavailable in some contexts
  }

  // Set favicon
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = 'image/png';
  link.href = branding.favicon;

  // ---------------------------------------------------------------------------
  // Dynamically override i18n resources with brand-specific text
  // Uses i18next's addResourceBundle with { deep: true, overwrite: true }
  // so only the specified keys are patched, leaving all other translations intact.
  // ---------------------------------------------------------------------------
  const locales: Array<{ lng: string; data: BrandI18n }> = [
    { lng: 'en', data: branding.i18n.en },
    { lng: 'zh', data: branding.i18n.zh },
  ];

  for (const { lng, data } of locales) {
    i18n.addResourceBundle(
      lng,
      'translation',
      {
        common: { appName: data.appName },
        chat: {
          disclaimer: data.disclaimer,
          replyPlaceholder: data.replyPlaceholder,
        },
        feedback: { submitInfo: data.submitInfo },
        login: { privacyNotice: data.privacyNotice },
        toast: { copyright: data.toastCopyright },
        version: { copyright: data.versionCopyright },
      },
      true, // deep merge
      true // overwrite existing keys
    );
  }
}
